// src/data/visaRequirements.ts

export interface PhotoSpecs {
  dimensions: string;
  background: string;
  rules: string[];
}

export interface VisaPrice {
  name: string;
  govtUsd: number;
  agentMarkupUsd: number;
  description: string;
  isHidden?: boolean;
}

export interface DestinationRequirement {
  countryCode: string;
  countryName: string;
  flag: string;
  photoSpecs: PhotoSpecs;
  requiredDocuments: string[];
  categoryDocuments?: Record<string, string[]>;
  entryRequirements: string[];
  visaPrices: VisaPrice[];
  borderGatesNote?: {
    title: string;
    landGates: string[];
    airGates: string[];
    seaGates: string[];
  };
}

export const VISA_REQUIREMENTS_DATA: Record<string, DestinationRequirement> = {
  INDIA: {
    countryCode: 'INDIA',
    countryName: 'India',
    flag: '🇮🇳',
    photoSpecs: {
      dimensions: '2 x 2 inches (51 x 51 mm) Square',
      background: 'Plain White or Light Off-White Background',
      rules: [
        'Recent photo taken within the last 6 months',
        'Full face, front view, eyes open, neutral facial expression',
        'No glasses (even prescription glasses are strictly prohibited)',
        'Head centered within the frame, showing full head from top of hair to bottom of chin',
        'File size: Min 10 KB, Max 1 MB (JPEG format)',
      ],
    },
    requiredDocuments: [
      'Original Passport bio page scan (valid for at least 6 months with 2 blank pages)',
      'Recent square passport-size photo with plain white background',
      'Confirmed Return Flight Ticket & Hotel Bookings',
      'Additional documents for Business / Medical e-Visas (Invitation letter, Business card, Hospital letter)',
    ],
    categoryDocuments: {
      TOURIST: [
        'Original Passport bio page scan (valid for at least 6 months with 2 blank pages)',
        'Recent square passport-size photo with plain white background',
        'Confirmed Return Flight Ticket & Hotel Bookings',
      ],
      BUSINESS: [
        'Copy of Passport page containing personal particulars (clear photo page with name, date of birth, nationality, expiry date, etc.)',
        'Copy of Business Card (showing applicant name, designation & company details)',
        'Copy of Business Invitation Letter (formal invitation letter from Indian Host Company/Firm on official letterhead)',
        'Required Applicant Company Details: Company Name*, Address & Phone No*, Website*, Nature of Business/Product*',
        'Document Language & Format: All uploaded documents (Business Cards, Invitation Letters, etc.) MUST be in English language (PDF format, Max 300 KB per file)',
        'Passport Verification: Uploaded Passport photo page must be of the same passport whose details are provided in Passport Details section, clear and legible (JPEG format, Min 10 KB - Max 1 MB for photo)',
      ],
      MEDICAL: [
        'Original Passport bio page scan (valid for at least 6 months with 2 blank pages)',
        'Recent square passport-size photo with plain white background',
        'Recommendation Letter from concerned Hospital in India on letterhead',
        'Medical history records & diagnosis from home country hospital',
        'Proof of relationship for e-Medical Attendant visa applicants',
      ],
    },
    entryRequirements: [
      'e-Visa is valid for entry through 31 designated International Airports & 5 Seaports',
      'Passport must have at least 6 months validity from date of arrival in India',
      'Biometric details will be mandatorily captured at Immigration checkpoint',
    ],
    visaPrices: [
      { name: 'e-Tourist Visa (30 Days - Apr to Jun)', govtUsd: 10, agentMarkupUsd: 15, description: 'Single/Double entry within 30 days', isHidden: false },
      { name: 'e-Tourist Visa (30 Days - Jul to Mar)', govtUsd: 25, agentMarkupUsd: 15, description: 'Multiple entry during peak tourist season', isHidden: false },
      { name: 'e-Tourist Visa (1 Year - Multiple Entry)', govtUsd: 40, agentMarkupUsd: 20, description: 'Multiple entries, 90 days stay per visit', isHidden: false },
      { name: 'e-Tourist Visa (5 Years - Multiple Entry)', govtUsd: 80, agentMarkupUsd: 25, description: 'Multiple entries, 90 days stay per visit', isHidden: false },
      { name: 'e-Business Visa (1 Year - Multiple Entry)', govtUsd: 80, agentMarkupUsd: 25, description: 'Multiple entries, 180 days max stay', isHidden: false },
      { name: 'e-Medical / Medical Attendant Visa (60 Days)', govtUsd: 80, agentMarkupUsd: 25, description: 'Triple entry within 60 days', isHidden: false },
    ],
  },

  MALAYSIA: {
    countryCode: 'MALAYSIA',
    countryName: 'Malaysia',
    flag: '🇲🇾',
    photoSpecs: {
      dimensions: '35 x 50 mm (Passport Standard)',
      background: 'Plain White Background',
      rules: [
        'Recent photo taken within the last 6 months',
        'No glasses, headwear or dark clothes obscuring face',
        'File size: Max 1 MB (JPEG format)',
      ],
    },
    requiredDocuments: [
      'Passport bio page scan (valid for at least 6 months)',
      'Recent studio photo (35x50mm white background)',
      'Confirmed Return Flight Ticket',
      'Hotel Booking or proof of accommodation in Malaysia',
      'Proof of sufficient funds (Bank statement / International Credit Card)',
    ],
    categoryDocuments: {
      TOURIST: [
        'Original Passport (valid for a minimum of 6 months)',
        'Previous Passport(s)',
        'Passport Size Photograph with a plain white background (35x50mm studio photo)',
        'Personal Cover / Request Letter',
        'Proof of Occupation: Approved Company Leave Letter OR Business Registration (in English)',
        'Bank Statement: Last 3 months\' transaction history',
        'Bank Balance Statement: Minimum ending balance of LKR 200,000 per applicant',
        'Bank Statement Address: MUST be addressed to: "The Visa Officer, High Commission of Malaysia, No. 26 A/5, Alwis Place, Colombo 03"',
        'Additional Documents: Bank Confirmation Letter, Copies of Previous Visa Pages (if available), EPF Confirmation Letter or Recent Tax Payment Slip (Required if applying via Business Registration)',
      ],
      BUSINESS: [
        'Passport bio page scan (valid for at least 6 months)',
        'Recent studio photo (35x50mm plain white background)',
        'Invitation Letter from Malaysian registered host company',
        'Confirmed Return Flight Ticket',
        'Hotel Booking confirmation',
      ],
    },
    entryRequirements: [
      'e-VISA Social Visit Pass is valid for 3 months from issue date',
      'Maximum stay of 30 days per entry (Non-extendable)',
      'Bank Balance Statement MUST be addressed to: The Visa Officer, High Commission of Malaysia, No. 26 A/5, Alwis Place, Colombo 03',
      'Must present Digital Arrival Card (MDAC) within 3 days prior to arrival',
    ],
    visaPrices: [
      { name: 'e-VISA Tourist (30 Days - Single Entry)', govtUsd: 35, agentMarkupUsd: 15, description: 'Valid for 3 months, 30 days max stay', isHidden: false },
      { name: 'e-VISA Tourist (30 Days - Multiple Entry)', govtUsd: 65, agentMarkupUsd: 20, description: 'Multiple entries within 3 - 12 months', isHidden: false },
      { name: 'e-VISA Student / Expatriate', govtUsd: 75, agentMarkupUsd: 25, description: 'For approved Malaysian institution enrollment', isHidden: false },
    ],
  },

  VIETNAM: {
    countryCode: 'VIETNAM',
    countryName: 'Vietnam',
    flag: '🇻🇳',
    photoSpecs: {
      dimensions: '4 x 6 cm (Passport Standard)',
      background: 'Plain White Background',
      rules: [
        'Recent, front-facing 4x6 cm digital photo',
        'Plain white background, no glasses, no teeth showing',
        'Passport Bio Page: High-quality color photo/scan, 6-month validity, visible bottom text',
      ],
    },
    requiredDocuments: [
      'Passport Bio Page: High-quality color photo/scan, 6-month validity, visible bottom text',
      'Passport Photo: Recent, front-facing, 4x6 cm digital photo, plain white background, no glasses, no teeth showing',
      'Travel Dates: Exact enter/exit dates',
      'Entry & Exit Points: Exact airport (e.g., Hanoi, Ho Chi Minh), non-changeable once issued',
      'Accommodation: Name and full address of hotel/stay in Vietnam',
    ],
    categoryDocuments: {
      TOURIST: [
        'Passport Bio Page: High-quality color photo/scan, 6-month validity, visible bottom text',
        'Passport Photo: Recent, front-facing, 4x6 cm digital photo, plain white background, no glasses, no teeth showing',
        'Travel Dates: Exact enter/exit dates',
        'Entry & Exit Points: Exact airport (e.g., Hanoi, Ho Chi Minh), non-changeable once issued',
        'Accommodation: Name and full address of hotel/stay in Vietnam',
        'Processing Time: 5-14 working days (Highly recommended: Apply at least 2 weeks before departure)',
      ],
      BUSINESS: [
        'Passport Bio Page scan (high-quality color photo, valid for at least 6 months)',
        'Passport Photo (4x6cm, plain white background, no glasses, no teeth showing)',
        'Invitation letter / approval letter from Vietnamese sponsor firm',
        'Confirmed flight itinerary & temporary hotel stay address',
      ],
    },
    entryRequirements: [
      'e-Visa issued for 30 days or 90 days (Single or Multiple Entry)',
      'Entry & Exit Points: Must enter and exit at the exact airport/port declared on e-Visa (Non-changeable once issued)',
      'Processing Time: 5-14 working days (Highly recommended to apply at least 2 weeks before departure)',
      'Allowed entry through 42 designated international border gates (airports, landports, seaports)',
    ],
    visaPrices: [
      { name: 'Vietnam e-Visa (30 Days - Single Entry)', govtUsd: 25, agentMarkupUsd: 15, description: 'Single Entry valid up to 30 days', isHidden: false },
      { name: 'Vietnam e-Visa (30 Days - Multiple Entry)', govtUsd: 50, agentMarkupUsd: 20, description: 'Multiple Entries valid up to 30 days', isHidden: false },
      { name: 'Vietnam e-Visa (90 Days - Single Entry)', govtUsd: 25, agentMarkupUsd: 15, description: 'Single Entry valid up to 90 days', isHidden: false },
      { name: 'Vietnam e-Visa (90 Days - Multiple Entry)', govtUsd: 50, agentMarkupUsd: 20, description: 'Multiple Entries valid up to 90 days', isHidden: false },
    ],
    borderGatesNote: {
      title: 'Electronic visas can only be used for entry and exit through border gates that are on the list approved by the Government of Viet Nam.',
      landGates: [
        '1. Bo Y international border gate, Kon Tum province.',
        '2. Cha Lo international border gate, Quang Binh province.',
        '3. Cau Treo international border gate, Ha Tinh province.',
        '4. Huu Nghi international border gate, Lang Son province.',
        '5. Ha Tien international border gate, Kien Giang province.',
        '6. Lao Bao international border gate, Quang Tri province.',
        '7. Lao Cai international border gate, Lao Cai province.',
        '8. La Lay international border gate, Quang Tri province.',
        '9. Moc Bai international border gate, Tay Ninh province.',
        '10. Mong Cai international border gate, Quang Ninh province.',
        '11. Nam Can international border gate, Nghe An province.',
        '12. Na Meo international border gate, Thanh Hoa province.',
        '13. Vinh Xuong international land and river border gate, An Giang province.',
        '14. Tinh Bien international border gate, An Giang province.',
        '15. Tay Trang international border gate, Dien Bien province.',
        '16. Xa Mat international border gate, Tay Ninh province.',
        '17. Dong Dang international border gate, Lang Son province',
        '18. Tra Linh international border gate, Cao Bang province',
        '19. Thanh Thuy international border gate, Tuyen Quang province',
        '20. Lao Cai international border gate (Railway), Lao Cai province',
        '21. Long Sap international border gate, Son La province',
        '22. Nam Giang international border gate, Da Nang province',
        '23. Le Thanh international border gate, Gia Lai province',
        '24. Tan Nam international border gate, Tay Ninh province',
        '25. Binh Hiep international border gate, Tay Ninh province',
        '26. Thuong Phuoc international border gate, Dong Thap province',
        '27. Dinh Ba international border gate, Dong Thap province',
      ],
      airGates: [
        '1. Cat Bi Airport Border Gate (HPH - Hai Phong)',
        '2. Cam Ranh Airport Border Gate (CXR - Nha Trang)',
        '3. Can Tho Airport Border Gate (VCA - Can Tho)',
        '4. Da Nang Airport Border Gate (DAD - Da Nang)',
        '5. Noi Bai Airport Border Gate (HAN - Hanoi)',
        '6. Phu Quoc Airport Border Gate (PQC - Phu Quoc)',
        '7. Phu Bai Airport Border Gate (HUI - Hue)',
        '8. Tan Son Nhat Airport Border Gate (SGN - Ho Chi Minh City)',
        '9. Van Don Airport Border Gate (VDO - Quang Ninh)',
        '10. Tho Xuan Airport Border Gate (THD - Thanh Hoa)',
        '11. Dong Hoi Airport Border Gate (VDH - Quang Binh)',
        '12. Phu Cat Airport Border Gate (UIH - Quy Nhon)',
        '13. Lien Khuong Airport Border Gate (DLI - Da Lat)',
        '14. Vinh Airport Border Gate (VII - Nghe An)',
        '15. Chu Lai Airport Border Gate (VCL - Quang Nam)',
      ],
      seaGates: [
        '1. Chan May Port Border Gate, Thua Thien Hue Province.',
        '2. Cam Pha Port Border Gate, Quang Ninh Province.',
        '3. Da Nang Port Border Gate, Da Nang City.',
        '4. Duong Dong Port Border Gate, Kien Giang Province.',
        '5. Dung Quat Port Border Gate, Quang Ngai Province.',
        '6. Hon Gai Port Border Gate, Quang Ninh Province.',
        '7. Hai Phong Port Border Gate, Hai Phong City.',
        '8. Nghi Son Port Border Gate, Thanh Hoa Province.',
        '9. Nha Trang Port Border Gate, Khanh Hoa Province.',
        '10. Quy Nhon port border gate, Binh Dinh province.',
        '11. Ho Chi Minh City Port Border Gate, Ho Chi Minh City.',
        '12. Vung Ang Port Border Gate, Ha Tinh Province.',
        '13. Vung Tau Port Border Gate, Ba Ria - Vung Tau Province.',
        '14. Van Gia Port Border Gate, Quang Ninh province',
        '15. Ninh Phuc Port Border Gate, Ninh Binh province',
        '16. Diem Dien Port Border Gate, Hung Yen province',
        '17. Hai Thinh Port Border Gate, Ninh Binh province',
        '18. Ben Thuy Port Border Gate, Nghe An province',
        '19. Sơn Duong Port Border Gate, Ha Tinh province',
        '20. Gianh Port Border Gate, Quang Tri province',
        '21. Hon La Port Border Gate, Quang Tri province',
        '22. Cua Viet Port Border Gate, Quang Tri province',
        '23. Thuan An Port Border Gate, Hue City',
        '24. Ky Ha Port Border Gate, Da Nang province',
        '25. Sa Ky Port Border Gate, Quang Ngai province',
        '26. Ca Na Port Border Gate, Khanh Hoa province',
        '27. Ninh Chu Port Border Gate, Khanh Hoa province',
        '28. Phu Quy Port Border Gate, Lam Dong province',
        '29. Dong Thap Port Border Gate, Dong Thap province',
        '30. My Thoi Port Border Gate, An Giang province',
        '31. Hon Chong Port Border Gate, An Giang province',
        '32. An Thoi Port Border Gate, An Giang province',
        '33. Ben Luc Port Border Gate, Tay Ninh province',
        '34. Soai Riep - Hiep Phuoc Port Border Gate, Dong Thap province',
        '35. Truong Long Hoa Port Border Gate, Vinh Long province',
        '36. Nam Can Port Border Gate, Ca Mau province',
        '37. Lien Huong Port Border Gate, Lam Dong province',
        '38. Giao Long Port Border Gate, Vinh Long province',
        '39. Vung Ro Port Border Gate, Dak Lak province',
      ],
    },
  },
};
